// Bulk operations component for enhanced table actions
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { auditTrail, getCurrentUserContext } from '../../lib/audit-trail';



interface BulkUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedItems: any[];
  onUpdate: (updates: any) => void;
  entityType: 'po' | 'invoice' | 'supplier' | 'customer';
}

export function BulkUpdateDialog({ 
  open, 
  onOpenChange, 
  selectedItems, 
  onUpdate, 
  entityType 
}: BulkUpdateDialogProps) {
  const [updateFields, setUpdateFields] = useState<Record<string, any>>({});
  const [updateReason, setUpdateReason] = useState('');
  const { toast } = useToast();

  const getAvailableFields = () => {
    switch (entityType) {
      case 'po':
        return [
          { key: 'status', label: 'Status', type: 'select', options: [
            { value: 'draft', label: 'Draft' },
            { value: 'review', label: 'Review' },
            { value: 'approved', label: 'Approved' },
            { value: 'delivered', label: 'Delivered' }
          ]},
          { key: 'priority', label: 'Priority', type: 'select', options: [
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
            { value: 'urgent', label: 'Urgent' }
          ]},
        ];
      case 'invoice':
        return [
          { key: 'status', label: 'Status', type: 'select', options: [
            { value: 'draft', label: 'Draft' },
            { value: 'pending', label: 'Pending' },
            { value: 'partial', label: 'Partial' },
            { value: 'paid', label: 'Paid' },
            { value: 'overdue', label: 'Overdue' }
          ]},
          { key: 'dueDate', label: 'Due Date', type: 'date' },
        ];
      case 'supplier':
        return [
          { key: 'status', label: 'Status', type: 'select', options: [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'pending_verification', label: 'Pending Verification' }
          ]},
          { key: 'paymentTerms', label: 'Payment Terms', type: 'select', options: [
            { value: 'net_15', label: 'Net 15' },
            { value: 'net_30', label: 'Net 30' },
            { value: 'net_45', label: 'Net 45' },
            { value: 'net_60', label: 'Net 60' }
          ]},
        ];
      default:
        return [];
    }
  };

  const handleApply = () => {
    if (Object.keys(updateFields).length === 0) {
      toast({
        title: "No changes selected",
        description: "Please select at least one field to update",
        variant: "destructive",
      });
      return;
    }

    if (!updateReason.trim()) {
      toast({
        title: "Reason required",
        description: "Please provide a reason for this bulk update",
        variant: "destructive",
      });
      return;
    }

    // Log bulk update in audit trail
    const userContext = getCurrentUserContext();
    selectedItems.forEach(item => {
      auditTrail.logEvent({
        entityType: entityType,
        entityId: item.id,
        action: 'bulk_update',
        userId: userContext.userId,
        changes: Object.entries(updateFields).map(([field, newValue]) => ({
          field,
          oldValue: item[field],
          newValue,
          changeReason: updateReason
        })),
        metadata: {
          bulkOperation: true,
          totalItemsUpdated: selectedItems.length,
          reason: updateReason
        }
      });
    });

    onUpdate({
      updates: updateFields,
      reason: updateReason,
      items: selectedItems
    });

    toast({
      title: "Bulk update completed",
      description: `Successfully updated ${selectedItems.length} items`,
    });

    onOpenChange(false);
    setUpdateFields({});
    setUpdateReason('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Update {entityType.toUpperCase()}s</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="flex items-center gap-2">
            <Badge variant="secondary">{selectedItems.length} items selected</Badge>
            <span className="text-sm text-muted-foreground">
              Changes will be applied to all selected items
            </span>
          </div>

          <Separator />

          <div className="space-y-4">
            <Label className="text-base font-medium">Fields to Update</Label>
            {getAvailableFields().map(field => (
              <div key={field.key} className="space-y-2">
                <Label>{field.label}</Label>
                {field.type === 'select' ? (
                  <Select 
                    value={updateFields[field.key] || '__none__'} 
                    onValueChange={(value) => setUpdateFields(prev => ({ ...prev, [field.key]: value === '__none__' ? '' : value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select {field.label.toLowerCase()}</SelectItem>
                      {field.options?.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.type === 'date' ? (
                  <Input 
                    type="date"
                    value={updateFields[field.key] || ''}
                    onChange={(e) => setUpdateFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                  />
                ) : (
                  <Input 
                    value={updateFields[field.key] || ''}
                    onChange={(e) => setUpdateFields(prev => ({ ...prev, [field.key]: e.target.value }))}
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <Label>Reason for Update *</Label>
            <Textarea 
              value={updateReason}
              onChange={(e) => setUpdateReason(e.target.value)}
              placeholder="Provide a clear reason for this bulk update (required for audit trail)"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>
            Apply Updates
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface BulkExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: any[];
  entityType: string;
}

export function BulkExportDialog({ open, onOpenChange, data, entityType }: BulkExportDialogProps) {
  const [exportFormat, setExportFormat] = useState('csv');
  const [includeFields, setIncludeFields] = useState<string[]>([]);
  const { toast } = useToast();

  const getExportFields = () => {
    if (data.length === 0) return [];
    
    const sample = data[0];
    return Object.keys(sample).map(key => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')
    }));
  };

  const handleExport = () => {
    const fieldsToInclude = includeFields.length > 0 ? includeFields : Object.keys(data[0] || {});
    
    if (exportFormat === 'csv') {
      const headers = fieldsToInclude;
      const rows = data.map(item => 
        fieldsToInclude.map(field => {
          const value = item[field];
          return typeof value === 'object' ? JSON.stringify(value) : value || '';
        })
      );

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${entityType}_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }

    toast({
      title: "Export completed",
      description: `Successfully exported ${data.length} ${entityType} records`,
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export {entityType} Data</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Export Format</Label>
            <Select value={exportFormat} onValueChange={setExportFormat}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csv">CSV</SelectItem>
                <SelectItem value="excel">Excel (XLSX)</SelectItem>
                <SelectItem value="json">JSON</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Fields to Include</Label>
            <div className="text-sm text-muted-foreground mb-2">
              Leave empty to include all fields
            </div>
            <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1">
              {getExportFields().map(field => (
                <div key={field.key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={field.key}
                    checked={includeFields.includes(field.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setIncludeFields(prev => [...prev, field.key]);
                      } else {
                        setIncludeFields(prev => prev.filter(f => f !== field.key));
                      }
                    }}
                  />
                  <Label htmlFor={field.key} className="text-sm">{field.label}</Label>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
            <Download className="h-4 w-4" />
            <span className="text-sm">
              Exporting {data.length} records
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}